import moment from "moment";
import { getAuthenticatedFetch } from "../../msal/authenticatedFetchService";
import { PeoplePlannerDisableInfoModel } from "../types/PeoplePlannerDisableInfoModel";
import { PeoplePlannerDisableInfoDTO } from "../types/PeoplePlannerDisableInfoDTO";
import { CareRosteringEnableInfoModel } from "../types/CareRosteringEnableInfoModel";
import { CareRosteringEnableInfoDTO } from "../types/CareRosteringEnableInfoDTO";

class CustomerService {
    getCustomerNameFromCustomerNamingConvention(customerNamingConvention: string, customerName: string) {
        if (!customerName)
            return '';
        
        return customerNamingConvention.replaceAll('{CustomerName}', customerName);
    }
    
    async getAllCustomers() {
        let url = `${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer`;
        let response = await getAuthenticatedFetch()(url);
        return await response.json();
    }

    async getCustomerByClientId(clientId: number) {
        let url = `${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}`;
        let response = await getAuthenticatedFetch()(url);
        return await response.json();
    }

    async generateMainDataExport(clientId: number) {
        let url = `${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}/generatemaindataexport`;
        let response = await getAuthenticatedFetch()(url);

        if (!response.ok) {
            throw new Error();
        }
        
        return await response.json();
    }

    async generateExternalDataExport(clientId: number) {
        let url = `${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}/generateexternaldataexport`;
        let response = await getAuthenticatedFetch()(url);
        
        if (!response.ok) {
            throw new Error();
        }
        
        return await response.json();
    }

    async getMainDataExportStatus(taskId: string) {
        let url = `${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/getmaindataexportstatus/${taskId}`;
        let response = await getAuthenticatedFetch()(url);

        if (!response.ok) {
            throw new Error();
        }

        return await response.json();
    }

    async getExternalDataExportStatus(taskId: string) {
        let url = `${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/getexternaldataexportstatus/${taskId}`;
        let response = await getAuthenticatedFetch()(url);

        if (!response.ok) {
            throw new Error();
        }

        return await response.json();
    }
    
    async requestCreateAppCustomer(companyName: string, countryId: number, environmentType: number) {
        const response = await getAuthenticatedFetch()(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/createcustomerrequest`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                countryId,
                companyName,
                environmentType
            })
        });

        if (!response.ok) {
            throw new Error();
        }

        return response.json();
    }

    async createAppCustomerStatus(taskId: string) {
        let url = `${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/createcustomerstatus/${taskId}`;
        let response = await getAuthenticatedFetch()(url);

        if (!response.ok) {
            throw new Error();
        }

        return response.json();
    }
    
    async deleteCustomer(clientId: number) {
        const response = await getAuthenticatedFetch()(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}/delete`, {
            method: 'POST'
        });

        const data = await response.json();

        return data;
    }
    
    async requestlockAppCustomer(clientId: number, salesForceId: string) {
        const response = await getAuthenticatedFetch()(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}/applockrequest`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                salesForceId: salesForceId
            })
        });
        
        if (!response.ok) {
            throw new Error();
        }

        return response.json();
    }

    async lockAppCustomerStatus(taskId: string) {
        let url = `${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/applockstatus/${taskId}`;
        let response = await getAuthenticatedFetch()(url);

        if (!response.ok) {
            throw new Error();
        }

        return response.json();
    }

    async getAppCustomerTerminations() {
        let url = `${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/appcustomerterminations`;
        let response = await getAuthenticatedFetch()(url);

        if (!response.ok) {
            throw new Error();
        }

        return response.json();
    }

    async unlockAppCustomer(environmentName: string, terminationsId: string) {
        let url = `${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/appunlockcustomer`;
        let response = await getAuthenticatedFetch()(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                environmentName,
                terminationsId
            })
        });
        
        if (!response.ok) {
            throw new Error();
        }

        return response;
    }

    async moveCustomer(clientId: number, appGrpName: string) {
        const response = await getAuthenticatedFetch()(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}/move/${appGrpName}`, {
            method: 'POST'
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error();
        }

        return data;
    }

    async getLastSignInDateTime(clientId: number) {
        let url = `${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}/lastsignindatetime`;
        let response = await getAuthenticatedFetch()(url);
        return await response.json();
    }

    async getPeoplePlannerDisableInfo(clientId: number, isOldPeoplePlanner: boolean): Promise<PeoplePlannerDisableInfoDTO> {
        let url = `${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}/peopleplannerdisableinfo?isOldPeoplePlanner=${isOldPeoplePlanner}`;
        let response = await getAuthenticatedFetch()(url);

        if (!response.ok) {
            throw new Error("Failed to fetch People Planner Disable Info");
        }
        
        const disableData = await response.json() as PeoplePlannerDisableInfoDTO;

        if (disableData.disablePeoplePlannerDate) {
            const utcDate = moment.utc(disableData.disablePeoplePlannerDate); 
            const localDate = utcDate.local();
            disableData.disablePeoplePlannerDate = localDate.toDate();
        }

        return disableData;
    }

    async updatePeoplePlannerDisableInfo(clientId: number, disablePeoplePlannerInfo: PeoplePlannerDisableInfoModel) {
        const response = await getAuthenticatedFetch()(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}/peopleplannerdisable`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                isOldPeoplePlanner: disablePeoplePlannerInfo.isOldPeoplePlanner,
                disablePeoplePlanner: disablePeoplePlannerInfo.disablePeoplePlanner,
                disablePeoplePlannerDate: disablePeoplePlannerInfo.disablePeoplePlannerDate
            })           
        });

        if (!response.ok) {
            const errorMessage = await response.text();
            throw new Error(`Failed to update People Planner Disable Info: ${errorMessage}`);
          }
    }

    async getCareRosteringEnableInfo(clientId: number): Promise<CareRosteringEnableInfoDTO> {
        let url = `${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}/carerosteringenableinfo`;
        let response = await getAuthenticatedFetch()(url);

        if (!response.ok) {
            throw new Error("Failed to fetch Care Rostering Enable Info");
        }
        
        const enableData = await response.json() as CareRosteringEnableInfoDTO;

        return enableData;
    }

    async updateCareRosteringEnableInfo(clientId: number, enableCareRosteringInfo: CareRosteringEnableInfoModel) {
        const response = await getAuthenticatedFetch()(`${process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}/Customer/${clientId}/carerosteringenable`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                enableCareRostering: enableCareRosteringInfo.enableCareRostering
            })           
        });

        if (!response.ok) {
            const errorMessage = await response.text();
            throw new Error(`Failed to update Care Rostering Enable Info: ${errorMessage}`);
          }
    }
}

export default new CustomerService();
